import { Reel } from "@quest/murka-games-behavior-panther";
import { gameConstants } from "../Constants/gameConstants";
import { clone, delayedCall, dispatcher, ingenuity, ITween, Linear } from "@quest/base-core";
import { eventDispatchers } from "../Constants/EventDispatchers";
import { GameParserModel } from "../Platform/GameParserModel";
import {
  AnimationSymbol,
  LayeredAnimationSymbol,
  SlotConstants,
  SlotEventConstants,
  SpineSymbol,
  StaticSymbol,
  SymbolConstants,
} from "@quest/slot-core";

export class ReelGame extends Reel {
  private newCasecadeReelGrid: number[];
  private reelLength: number = 10;

  /** update on spin */
  public updateSpin(deltaTime: number) {
    if (!this.spinNow) {
      return;
    }
    let id: number;
    let icon;
    let sym;
    // Checking whether the reel has moved beyond the symbol height, if yes then its y is reset to 0 and loop is incremented.
    if (this.initScroll >= this.model.getSymbolHeight()) {
      // loop incremented
      this.intLoopCounter++;
      // variable which changes y is reset to 0.
      this.initScroll = 0;
      //  flag to check if we need to remove symbol from list
      let removeSymbolFromList: boolean = false;
      // shifting each symbol one position down and removing symbol which go two positions down the visible area
      this.symCollection.forEach((symbol, index) => {
        if (typeof symbol !== "undefined") {
          icon = symbol.getStatic();
          //  Based on spinDirection, update grid position on reel.
          if (this.spinDirection === 1) {
            symbol.setYPosition(symbol.y + this.model.getSymbolHeight() + (this.model.getSymbolSpacingY() || 0));
            symbol.gridPosition++;
          } else {
            symbol.setYPosition(symbol.y - (this.model.getSymbolHeight() + (this.model.getSymbolSpacingY() || 0)));
            symbol.gridPosition--;
          }
          icon.visible = true;
          symbol.forceHide = false;
          if (symbol.json.spinStartSym) {
            let spinIcon = symbol.getSpinStartSymAnimation();
            if (
              !((symbol.gridPosition > this.rows && this.spinDirection === 1) || (symbol.gridPosition < -1 && this.spinDirection === 0))
            ) {
              spinIcon.visible = true;
              this.addChildAt(spinIcon, symbol.gridPosition);
              if (spinIcon instanceof StaticSymbol || spinIcon instanceof SpineSymbol) {
                spinIcon.playAnim(SymbolConstants.DEFAULT_ANIMATION_KEY);
              } else if (spinIcon instanceof AnimationSymbol) {
                spinIcon.playAnim(SymbolConstants.DEFAULT_ANIMATION_KEY, undefined, () => {
                  spinIcon.visible = false;
                  this.removeChild(spinIcon);
                });
              } else if (spinIcon instanceof LayeredAnimationSymbol) {
                spinIcon.playAnim();
              }
            }
          }
          //  Check for gridPosition based on spinDirection. If symbol is offscreen, remove it.
          if ((symbol.gridPosition > this.rows && this.spinDirection === 1) || (symbol.gridPosition < -1 && this.spinDirection === 0)) {
            if (symbol.json.spinSym) {
              const spinIcon = symbol.getSpinAnimation();
              spinIcon.stopAnim();
            }
            symbol.returnToPool();
            // make the symbol undefined. This is to ensure loop runs through each element.
            this.symCollection[index] = undefined;
            removeSymbolFromList = true;
            icon.off(SlotEventConstants.SYMBOL_ANIM_STARTED);
            icon.off(SlotEventConstants.SYMBOL_ANIM_ENDED);
          }
        }
      });
      // The flag is true and loop is complete. Remove symbol based on direction.
      if (removeSymbolFromList) {
        const len = this.symCollection.length;
        for (let i: number = len - 1; i >= 0; i--) {
          if (typeof this.symCollection[i] === "undefined") {
            this.symCollection.splice(i, 1);
          }
        }
      }
      // checking if the stops have been set then pick symbols from stop positions otherwise pick from the reelset
      // intMaxLoops is used for anticipation spin and for asymmetric reels.
      if (Array.isArray(this.stops) && this.stops.length > 0 && this.intLoopCounter >= this.intMaxLoops) {
        // DJ :: based on direction, get the stop symbol ID (from top or bottom) and increment/decrement stopIndex
        // DJ :: Add symbol to collection based on direction. It should be top or bottom.
        // DJ :: Symbol position in grid is dependent on direction.
        if (this.spinDirection === 1) {
          const id: number = this.stops[this.stopIndex - 1] as number;
          sym = this.iconsPool.getSymbol(id);
          this.symCollection.unshift(sym);
          sym.gridPosition = -1;
          this.stopIndex--;
        } else {
          const id: number = this.stops[this.stopIndex] as number;
          sym = this.iconsPool.getSymbol(id);
          this.symCollection.push(sym);
          sym.gridPosition = this.rows;
          this.stopIndex++;
        }
        icon = sym.getStatic();
        icon.visible = true;
        if (ingenuity.configData.decreaseSpinSpeed) {
          if (this.model.json.decSpinSpeed) {
            this.initSpinSpeed =
              this.initSpinSpeed > this.model.json.minSpeed ? this.initSpinSpeed - this.model.json.decSpinSpeed : this.model.json.minSpeed;
          } else {
            this.initSpinSpeed--;
          }
          this.quickSpinSpeed--;
        }
        if (!icon.hasEvent(SlotEventConstants.SYMBOL_ANIM_STARTED)) {
          icon.on(SlotEventConstants.SYMBOL_ANIM_STARTED, this.onSymbolAnimationEvent, this);
        }
        if (!icon.hasEvent(SlotEventConstants.SYMBOL_ANIM_ENDED)) {
          icon.on(SlotEventConstants.SYMBOL_ANIM_ENDED, this.onSymbolAnimationEvent, this);
        }
      } else {
        // DJ :: Based on direction, we need to pick index to get next symbol from reel.
        // DJ :: Add symbol to collection based on direction. It should be top or bottom.
        // DJ :: Symbol position in grid is dependent on direction.
        if (this.spinDirection === 1) {
          id = this.model.getCurrentReelset()[this.reelId][this.index];
          sym = this.iconsPool.getSymbol(id);
          this.symCollection.unshift(sym);
          sym.gridPosition = -1;
        } else {
          id = this.model.getCurrentReelset()[this.reelId][this.upindex];
          sym = this.iconsPool.getSymbol(id);
          this.symCollection.push(sym);
          sym.gridPosition = this.rows;
        }
        if (sym.json.spinSym) {
          icon = sym.getSpinAnimation();
          this.playSymbolSpinAnimation(icon);
        } else if (this.blurSpin) {
          icon = sym.getBlur();
          this.fadeSpinningSymbolOnAnticipation(icon);
        } else {
          icon = sym.getStatic();
        }
        icon.visible = true;
      }
      // DJ :: Position where symbol needs to be added in reel.
      let yPos: number = 0;
      if (this.spinDirection === 1) {
        yPos = -this.model.getSymbolHeight() - (this.model.getSymbolSpacingY() || 0);
      } else {
        yPos = this.model.getSymbolHeight() * this.rows + (this.model.getSymbolSpacingY() || 0);
      }
      sym.setYPosition(yPos);
      (sym.parent as any) = this;
      this.addChildAt(icon, 0);
      // DJ :: Once all symbols are displayed, we need to stop reels.
      // Code below running in loop checks stopSpinNow and if true, ends spin.
      if ((this.spinDirection === 1 && this.stopIndex === 0) || (this.spinDirection === 0 && this.stopIndex === this.rows + 2)) {
        this.stopSpinNow = true;
      }
    }
    // moving the reels up or down based on the spin direction
    this.y =
      this.spinDirection === 1
        ? this.model.reelJson[this.reelId].y + this.initScroll
        : -(this.model.reelJson[this.reelId].y + this.initScroll);
    let delta: number = deltaTime / this.desiredFPSTime,
      speed,
      qSpeed;
    if (delta > 1) {
      qSpeed = this.quickSpinSpeed * delta;
      speed = this.initSpinSpeed * delta;
    } else {
      qSpeed = this.quickSpinSpeed;
      speed = this.initSpinSpeed;
    }
    if (this.model.quickSpin) {
      this.initScroll += qSpeed + this.anticipationSpeed;
    } else {
      this.initScroll += speed + this.anticipationSpeed;
    }
    if (ingenuity.configData.hasOverlaySymbols) {
      this.sortIconsOnType();
    }
    // this block of code is executed when all the symbols have been placed and bounce before the stop is played
    // reel_stopping event is fired
    if (this.stopSpinNow) {
      this.spinNow = false;
      this.handleReelStop();
      if (!this.model.noStopBounce) {
        // let stopBounceAmt = this.model.getReelConfig(this.reelId).stopBounceAmt || (this.model.getSymbolHeight() * 0.15);
        const stopBounceAmt = Array.isArray(this.model.stopBounceAmt) ? this.model.stopBounceAmt[this.reelId] : this.model.stopBounceAmt;
        // let stopBounceTime = this.model.getReelConfig(this.reelId).stopBounceTime || 150;
        const stopBounceTime = Array.isArray(this.model.stopBounceTime)
          ? this.model.stopBounceTime[this.reelId]
          : this.model.stopBounceTime;
        // let stopBounceTimeout = this.model.getReelConfig(this.reelId).stopBounceTimeout || 100;
        const stopBounceTimeout = Array.isArray(this.model.stopBounceTimeout)
          ? this.model.stopBounceTimeout[this.reelId]
          : this.model.stopBounceTimeout;
        const bounce = this.game.add.tween(this);
        // DJ :: Stop bounce effect based on direction to stop reels.
        bounce.to(
          {
            y: this.spinDirection === 1 ? this.y + stopBounceAmt : this.y - stopBounceAmt,
          },
          stopBounceTime,
          ingenuity.configData.reelStopBounceInEase,
          false
        );
        bounce.to(
          {
            y: this.spinDirection === 1 ? this.model.reelJson[this.reelId].y : -this.model.reelJson[this.reelId].y,
          },
          stopBounceTimeout,
          ingenuity.configData.reelStopBounceOutEase,
          false
        );
        bounce.onComplete.add(this.spinDone, this);
        bounce.start();
      } else {
        delayedCall("stopReel", 10, this.spinDone, this);
      }
    }
  }

  // Override to match complete grid in reel set and find index
  public setStops(stopsArray: any) {
    var newStops;
    if (this.model.isReelLocked(this.reelId)) {
      this.spinDone();
      return;
    }
    this.reelSet = this.model.getCurrentReelset()[this.reelId];
    if (Array.isArray(stopsArray)) {
      newStops = clone(stopsArray);
      this.indx = this.reelSet.indexOf(newStops[0]);
      this.indx = this.indx < 1 ? this.reelSet.length : this.indx;
      newStops.unshift(this.reelSet[--this.indx]);
      var lastIcon = this.indx + this.rows;
      lastIcon = lastIcon >= this.reelSet.length - 1 ? 0 : lastIcon;
      newStops.push(this.reelSet[++lastIcon]);
      this.stops = newStops;
      this.model.setReelGrid(this.reelId, stopsArray);
    } else {
      stopsArray = Number(stopsArray);
      stopsArray -= ingenuity.configData.reelStopRow;
      stopsArray = stopsArray < 0 ? this.reelSet.length + stopsArray : stopsArray;
      newStops = this.reelSubset(stopsArray);
      this.indx = stopsArray;
      this.model.setReelGrid(this.reelId, newStops);
      this.stops = newStops;
    }
    this.stopIndex = this.spinDirection === 1 ? newStops.length : 0;
    this.intLoopCounter = 0;
  }

  /**
   * This method wil execute the bound of the reel after cascading symbols slide in
   */
  private bounceAfterCascade(): void {
    // this block of code is executed when all the symbols have been placed and bounce before the stop is played
    if (!this.model.noStopBounce) {
      const stopBounceAmt: string = gameConstants.STOP_CASCADE_BOUNCE;
      const stopBounceTime: number = gameConstants.STOP_CASCADE_BOUNCE_TIME;
      const stopBounceTimeout: number = gameConstants.STOP_CASCADE_BOUNCE_TIMEOUT;
      const bounce: ITween = this.game.add.tween(this);
      bounce
        .to({ y: this.y + stopBounceAmt }, stopBounceTime, ingenuity.configData.reelStopBounceInEase, false)
        .chain()
        .to({ y: this.model.reelJson[this.reelId].y }, stopBounceTimeout, ingenuity.configData.reelStopBounceOutEase, false);
      // bounce.to({ y: this.model.reelJson[this.reelId].y }, stopBounceTimeout, ingenuity.configData.reelStopBounceOutEase, false);
      bounce.start();
      bounce.onComplete.addOnce(() => {
        if (ingenuity.currentGame.state.getCurrentState().key === SlotConstants.baseGameState) {
          dispatcher.fireEvent(eventDispatchers.CASCADE_SPIN_COMPLETE);
        } else {
          dispatcher.fireEvent(eventDispatchers.FG_CASCADE_SPIN_COMPLETE);
        }
      }, this);
    }
  }

  /**
   * function will remove burst symbol from reel
   * and tween remaining symbols in row(reel)- bottom to top
   * after tweening updates the reel with new reelset
   */
  protected dissolveSymbolPositions(reelId: number): void {
    let parent: any = this.parent;
    const CurrReelBurstOffsets: number[][] = this.getCurrentReelBurstOffsets(reelId);
    if (CurrReelBurstOffsets.length !== 0) {
      const reelBurstSymIndex: number[] = this.findReelBurstIndex(CurrReelBurstOffsets);
      let orginalSym: any = [];
      const numA: number[] = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9];
      this.newCasecadeReelGrid = (ingenuity.parserModel as GameParserModel).getReelGrid()[reelId];
      let diff: number[] = numA.filter(function (x) {
        return reelBurstSymIndex.indexOf(x) < 0;
      });
      diff.reverse();
      for (let i: number = 0; i < this.reelLength; i++) {
        orginalSym.push(parent.getSymbolByPos(reelId, i).getStatic());
      }
      const diffLenght: number = diff.length;
      for (let i: number = 0; i < diffLenght; i++) {
        const dropSym = parent.getSymbolByPos(reelId, diff[i]).getStatic();
        const drop = orginalSym.pop();
        const dropTween: ITween = this.game.add.tween(dropSym.position);
        dropTween.to({ y: drop.position.y }, gameConstants.REEL_DROP_TWEEN_DURATION, Linear.None);
        dropTween.start();
        dropTween.onComplete.add(() => {
          this.init(this.newCasecadeReelGrid);
          if (i === diffLenght - 1) {
            this.bounceAfterCascade();
          }
        }, this);
      }
    }
  }

  // Add to play bonus landing sound
  protected playBonusLandingSoundInCascade() {
    const cascadeReelGrid: number[][] = (ingenuity.parserModel as GameParserModel).getReelGrid();
    const cascadePreviousReelGrid: number[][] = (ingenuity.parserModel as GameParserModel).getBaseGameGridForStack();
    let position: number[] = [];
    for (let i: number = 0; i < cascadeReelGrid.length; i++) {
      for (let j: number = 3; j < cascadeReelGrid[i].length; j++) {
        if (cascadeReelGrid[i][j] === gameConstants.BONUS_SYMBOL_GAME) {
          position.push(i);
        }
      }
    }
    if (position.length) {
      for (let l: number = 0; l < position.length; l++) {
        let symbolExistsInPreviousGrid: boolean = false;
        for (let k: number = 3; k < cascadePreviousReelGrid[position[l]].length; k++) {
          if (cascadePreviousReelGrid[position[l]][k] === gameConstants.BONUS_SYMBOL_GAME) {
            symbolExistsInPreviousGrid = true;
            break;
          }
        }
        if ((ingenuity.parserModel as GameParserModel).getCascadingCount() <= 1) {
          if (!symbolExistsInPreviousGrid && (ingenuity.parserModel as GameParserModel).getHasFreeGamesWin()) {
            // (ingenuity.soundManager as any).playSound(SoundConstantGame.BONUS_LANDING_IN_CASCADE, gameConstants.FULL_VOLUME, false, gameConstants.SOUND_LIST_PRELOAD);
          }
        } else {
          if (!symbolExistsInPreviousGrid) {
            // (ingenuity.soundManager as any).playSound(SoundConstantGame.BONUS_LANDING_IN_CASCADE, gameConstants.FULL_VOLUME, false, gameConstants.SOUND_LIST_PRELOAD);
          }
        }
      }
    }
  }
  /**
   * This method finds the drop index of the burst symbols
   * @param arr array of win symbols offsets
   * @returns burst symbols array
   */
  private findReelBurstIndex(arr: number[][]): number[] {
    const secondIndexArray = arr.map((subArray: number[]) => subArray[1]);
    return secondIndexArray;
  }

  /**
   * This method get the current reel busrst symbols offsets
   * @param reelId  current spining reel no
   * @returns current spining reel burst/cascade symbols offsets
   */
  private getCurrentReelBurstOffsets(reelId: number): number[][] {
    const cascadeOffsets: number[][][] = (ingenuity.parserModel as GameParserModel).getCascadeOffsets();
    const currentReelBurstOffsets = cascadeOffsets[0].filter((item: any) => item[0] === reelId);
    return currentReelBurstOffsets;
  }

  /**
   * Method overrided to play symbol animation
   */
  public playAnimByPos(pos: any, symReel: any, opts: any) {
    let parent: any = this.parent;
    const sym = parent.getSymbolByPos(this.reelId, pos);
    sym.getStatic().visible = false;
    const icon = sym.getAnimation();
    if (!icon) {
      return;
    }
    if (!icon.hasEvent(SlotEventConstants.SYMBOL_ANIM_STARTED)) {
      icon.on(SlotEventConstants.SYMBOL_ANIM_STARTED, this.onSymbolAnimationEvent, this);
    }
    if (!icon.hasEvent(SlotEventConstants.SYMBOL_ANIM_ENDED)) {
      icon.on(SlotEventConstants.SYMBOL_ANIM_ENDED, this.onSymbolAnimationEvent, this);
    }
    if (
      icon.num !== gameConstants.MAN_SYMBOL_1X3_STACK_ID &&
      icon.num !== gameConstants.MAN_SYMBOL_1X5_STACK_ID
    ) {
      symReel.addChild(icon);
    }
    icon.visible = !sym.forceHide;
    this.hiddenIcons.push(icon);
    if (!this.checkSymbolInArray(icon)) {
      this.AllIcons.push(icon);
    }
    const staticAnim = icon.json.animations ? icon.json.animations.static : "static";
    const winAnim = icon.json.animations ? icon.json.animations.anim : "animation";
    icon.playAnim(staticAnim, false, 0);
    setTimeout(() => {
      icon.playAnim(winAnim);
    }, 10);
  }
}
